import React, { useState, useEffect } from "react";
import { Modal, Box, Typography, TextField, Button } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import SelectAgentsKeyManager from "components/form/SelectAgentKeyManager";
import { useForm } from "react-hook-form";

const AgentAproveReject = ({
  isOpen,
  onClose,
  title,
  message = "",
  billStatus,
  onYesClick,
  newStatus,
  onRejectClick,
  customClass = "",
  loading,
  keyManager,
}) => {
  const [remark, setRemark] = useState("");
  const [error, setError] = useState("");
  const [rejectError, setRejectError] = useState("");
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [selectedKeyManager, setSelectedKeyManager] = useState(
    keyManager || "",
  );

  const { register } = useForm();

  // Update selected transport head when keyManager changes
  useEffect(() => {
    setSelectedKeyManager(keyManager || "");
  }, [keyManager]);

  const adminInfo = localStorage.getItem("adminInfo")
    ? JSON.parse(localStorage.getItem("adminInfo"))
    : null;

  const userRole = adminInfo?.profile;

  // Reset states when modal closes
  useEffect(() => {
    if (!isOpen) {
      setRemark("");
      setError("");
      setRejectError("");
      setIsRejectModalOpen(false);
      setSelectedKeyManager(keyManager || "");
    }
  }, [isOpen, keyManager]);

  // Open reject modal
  const handleReject = () => {
    setRejectError("");
    setIsRejectModalOpen(true);
  };

  // Approve
  const handleApprove = () => {
    if (!selectedKeyManager) {
      setError("Please select a key manager!");
      return;
    }

    setError("");

    onYesClick({
      key_manager_id: selectedKeyManager,
    });
  };

  // Reject submit
  const handleRejectSubmit = () => {
    if (!remark.trim()) {
      setRejectError("Remark is required!");
      return;
    }

    setRejectError("");

    onRejectClick({
      remark: remark.trim(),
      key_manager_id: selectedKeyManager,
    });

    setIsRejectModalOpen(false);
  };

  // Close reject modal
  const handleRejectClose = () => {
    setRejectError("");
    setIsRejectModalOpen(false);
  };

  // Transport head selection
  const handleTransportHeadChange = (value) => {
    setSelectedKeyManager(value);

    if (value) {
      setError("");
    }
  };

  // Close main modal
  const handleMainModalClose = () => {
    setError("");
    setRejectError("");
    setRemark("");
    setSelectedKeyManager("");

    onClose();
  };

  return (
    <div className={customClass}>
      {/* Main Modal */}
      <Modal
        open={isOpen}
        onClose={handleMainModalClose}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            width: "400px",
            borderRadius: "8px",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 2,
            }}
          >
            <Typography
              variant="h6"
              id="modal-title"
              sx={{ textAlign: "left" }}
            >
              {title}
            </Typography>

            <button
              onClick={handleMainModalClose}
              className="text-gray-500 hover:text-gray-800 transition duration-200"
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                fontSize: "18px",
              }}
              aria-label="Close"
            >
              ✕
            </button>
          </Box>

          {/* Transport Head Select */}
          {!keyManager && (
            <>
              <SelectAgentsKeyManager
                register={register}
                name="transport_head_id"
                label="Branch Manager"
                selectOption="Select Branch Manager"
                selectedKeyManager={selectedKeyManager}
                setSelectedKeyManager={handleTransportHeadChange}
              />

              {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
            </>
          )}

          {/* Message */}
          <Box
            sx={{
              textAlign: "center",
              marginBottom: 2,
              marginTop: 2,
            }}
          >
            {message && (
              <Typography sx={{ color: "text.secondary" }}>
                {message}
              </Typography>
            )}
          </Box>

          {/* Actions */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "30px",
            }}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: 1,
                }}
              >
                {/* Reject */}
                <Button
                  variant="text"
                  onClick={handleReject}
                  disabled={loading}
                  sx={{
                    color: "red",
                    marginRight: 2,
                  }}
                >
                  Reject
                </Button>

                {/* Approve */}
                <Button
                  variant="text"
                  onClick={handleApprove}
                  disabled={billStatus === "loss" && userRole !== "Admin"}
                  sx={{
                    color: "green",
                    marginRight: 2,
                  }}
                >
                  Approve
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>

      {/* Reject Modal */}
      <Modal
        open={isRejectModalOpen}
        onClose={handleRejectClose}
        aria-labelledby="reject-modal-title"
        aria-describedby="reject-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            width: "400px",
            borderRadius: "8px",
          }}
        >
          {/* Reject Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 2,
            }}
          >
            <Typography
              variant="h6"
              id="reject-modal-title"
              sx={{ textAlign: "left" }}
            >
              Tell us the reason
            </Typography>

            <button
              onClick={handleRejectClose}
              className="text-gray-500 hover:text-gray-800 transition duration-200"
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                fontSize: "18px",
              }}
              aria-label="Close"
            >
              ✕
            </button>
          </Box>

          {/* Reason Input */}
          <Box
            sx={{
              textAlign: "center",
              marginBottom: 2,
            }}
          >
            <TextField
              value={remark}
              inputProps={{ maxLength: 200 }}
              onChange={(e) => {
                setRemark(e.target.value);

                if (e.target.value.trim()) {
                  setRejectError("");
                }
              }}
              label="Reason for reject"
              fullWidth
              variant="outlined"
              error={Boolean(rejectError)}
              helperText={rejectError || `${remark.length}/200`}
              sx={{ marginTop: 2 }}
            />
          </Box>

          {/* Reject Actions */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="text"
              onClick={handleRejectClose}
              sx={{
                color: "red",
                marginRight: 2,
              }}
            >
              Cancel
            </Button>

            <Button
              variant="text"
              onClick={handleRejectSubmit}
              sx={{
                color: "green",
                marginRight: 2,
              }}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default React.memo(AgentAproveReject);
